import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { G, HOW, FAQ, TIERS } from "@/brand";
import api, { apiError } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { ChevronDown, MessageSquarePlus, Heart, Copy, Check, Mail } from "lucide-react";
import { FeedbackModal } from "@/components/FeedbackModal";
import { DonationModal } from "@/components/DonationModal";

export default function Landing() {
  const nav = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [fb, setFb] = useState(false);
  const [donate, setDonate] = useState(false);
  const [featured, setFeatured] = useState(null);
  const [copied, setCopied] = useState(false);
  const [subEmail, setSubEmail] = useState("");
  const [subDone, setSubDone] = useState(false);
  const invitedRef = new URLSearchParams(window.location.search).get("ref");

  useEffect(() => { api.get("/featured").then(({ data }) => setFeatured(data && data.id ? data : null)).catch(() => {}); }, []);

  const copyFeatured = () => {
    if (!featured) return;
    navigator.clipboard.writeText(featured.text).catch(() => {});
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const subscribe = async () => {
    if (!subEmail.trim()) return;
    try { await api.post("/subscribe", { email: subEmail, ref: invitedRef }); setSubDone(true); setSubEmail(""); } catch { setSubDone(true); }
  };

  return (
    <div style={{ background: G.black, minHeight: "100vh", position: "relative", zIndex: 0, overflowX: "hidden" }}>
      {/* Scroll-locked ghost watermark — fixed to viewport, stays centered as you scroll */}
      <div aria-hidden="true" style={{
        position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
        fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontWeight: 700,
        fontSize: "clamp(68px,16vw,262px)", lineHeight: 1, whiteSpace: "nowrap",
        color: "rgba(201,168,76,0.06)", WebkitTextStroke: "1px rgba(201,168,76,0.11)",
        letterSpacing: "0.04em", userSelect: "none", pointerEvents: "none", zIndex: -1,
      }}>Prompt Savant</div>
      <Nav />

      {/* HERO */}
      <section style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "160px 24px 100px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.035, backgroundImage: `linear-gradient(${G.gold} 1px,transparent 1px),linear-gradient(90deg,${G.gold} 1px,transparent 1px)`, backgroundSize: "72px 72px" }} />
        <div style={{ position: "absolute", top: "5%", left: "50%", transform: "translateX(-50%)", width: 700, height: 700, borderRadius: "50%", background: `radial-gradient(circle,${G.gold}0d 0%,transparent 65%)`, pointerEvents: "none" }} />
        <div className="mono fade-up" style={{ fontSize: 10, letterSpacing: "0.5em", color: G.gold, marginBottom: 28, position: "relative", zIndex: 2 }}>
          THE AI MUSIC PROMPT LIBRARY
        </div>
        <h1 className="fade-up-1 glow-gold" style={{ fontSize: "clamp(40px,8vw,86px)", fontWeight: 700, lineHeight: 1.02, color: "#fff", maxWidth: 1000, position: "relative", zIndex: 2, letterSpacing: "-0.02em" }}>
          Start free.<br /><span style={{ fontStyle: "italic", fontWeight: 300, color: G.gold }}>Go deep when you're ready.</span>
        </h1>
        <p className="fade-up-2" style={{ fontSize: "clamp(16px,2vw,20px)", color: G.muted, maxWidth: 640, marginTop: 28, lineHeight: 1.6, fontStyle: "italic", position: "relative", zIndex: 2 }}>
          50 genres. Up to 10,050 codex-grade AI music generation prompts — built for maximum output, minimum waste.
        </p>
        <div className="fade-up-3" style={{ display: "flex", gap: 14, marginTop: 40, flexWrap: "wrap", justifyContent: "center", position: "relative" }}>
          <button data-testid="hero-start-free" onClick={() => nav("/library")} className="btn-gold" style={{ padding: "15px 34px", fontSize: 11 }}>
            Start Free — No Card Needed
          </button>
          <button data-testid="hero-view-upgrades" onClick={() => nav("/pricing")} className="btn-outline" style={{ padding: "15px 34px", fontSize: 11 }}>
            View Upgrades
          </button>
        </div>
        <div className="fade-up-4" style={{ display: "flex", gap: 40, marginTop: 64, flexWrap: "wrap", justifyContent: "center", position: "relative" }}>
          {[["50", "Prompt Savant · Free"], ["2,050", "Vault · $19.99"], ["10,050", "Codex · $59.99"]].map(([n, l], i) => (
            <div key={n} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 40, fontWeight: 700, color: i === 2 ? G.orange : G.gold }}>{n}</div>
              <div className="mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: G.goldDim, marginTop: 4 }}>{l.toUpperCase()}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ background: "#000000", borderTop: `1px solid ${G.border}33` }}>
      <div style={{ padding: "100px 5vw", maxWidth: 1200, margin: "0 auto" }}>
        <SectionLabel>HOW IT WORKS</SectionLabel>
        <h2 style={heading}>Simple. Immediate. Yours.</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 24, marginTop: 50 }}>
          {HOW.map((h) => (
            <div key={h.n} style={{ borderLeft: `1px solid ${G.border}`, paddingLeft: 20 }}>
              <div className="mono" style={{ fontSize: 28, color: G.gold, fontWeight: 700 }}>{h.n}</div>
              <div style={{ fontSize: 22, color: "#fff", marginTop: 10, fontWeight: 400 }}>{h.t}</div>
              <div style={{ color: G.muted, fontSize: 14, marginTop: 8, lineHeight: 1.6, fontStyle: "italic" }}>{h.d}</div>
            </div>
          ))}
        </div>
      </div>
      </section>

      {/* PROMPT OF THE DAY */}
      {featured && (
        <section style={{ padding: "70px 5vw", maxWidth: 900, margin: "0 auto" }}>
          <SectionLabel>PROMPT OF THE DAY</SectionLabel>
          <div className="tier-card" style={{ background: G.dark, border: `1px solid ${G.border}`, padding: "30px 28px", marginTop: 20 }}>
            <div className="mono" style={{ fontSize: 9, letterSpacing: ".25em", color: G.gold, marginBottom: 10 }}>
              {featured.genre_name?.toUpperCase()} · {featured.bpm}
            </div>
            <div data-testid="featured-text" style={{ fontSize: 15.5, lineHeight: 1.7, color: G.text, maxHeight: 150, overflow: "hidden" }}>{featured.text}</div>
            <button data-testid="featured-copy" onClick={copyFeatured} className="btn-outline" style={{ marginTop: 18, padding: "10px 20px", fontSize: 10, display: "inline-flex", alignItems: "center", gap: 8 }}>
              {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy Prompt"}
            </button>
          </div>
        </section>
      )}

      {/* TIERS PREVIEW */}
      <section style={{ padding: "60px 5vw 100px", maxWidth: 1200, margin: "0 auto" }}>
        <SectionLabel>THREE TIERS · ONE DECISION</SectionLabel>
        <h2 style={heading}>Every tier accesses the same codex — at different depths.</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 22, marginTop: 50 }}>
          {TIERS.map((t) => (
            <div key={t.id} className="tier-card" data-testid={`landing-tier-${t.id}`} style={{ background: G.tier, border: `1px solid ${t.badge ? G.orange : G.border}`, padding: "32px 28px" }}>
              {t.badge && <div className="mono" style={{ fontSize: 8, letterSpacing: "0.3em", color: G.orange, marginBottom: 10 }}>★ {t.badge}</div>}
              <div className="mono" style={{ fontSize: 9, letterSpacing: "0.25em", color: G.goldDim }}>{t.sub}</div>
              <div style={{ fontSize: 24, color: "#fff", marginTop: 8, fontStyle: "italic" }}>{t.name}</div>
              <div style={{ fontSize: 42, fontWeight: 700, color: t.accent, marginTop: 14 }}>{t.price}</div>
              <div className="mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: G.goldDim, marginTop: 4 }}>{t.priceSub}</div>
              <button data-testid={`landing-tier-cta-${t.id}`} onClick={() => nav(t.id === "free" ? "/library" : "/pricing")} className={t.outline ? "btn-outline" : t.badge ? "btn-orange" : "btn-gold"} style={{ width: "100%", padding: 13, fontSize: 10, marginTop: 22 }}>
                {t.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: "80px 5vw 100px", borderTop: `1px solid ${G.border}33`, maxWidth: 820, margin: "0 auto" }}>
        <SectionLabel>FAQ</SectionLabel>
        <h2 style={heading}>Questions Answered</h2>
        <div style={{ marginTop: 40 }}>
          {FAQ.map((f, i) => (
            <div key={f.q} data-testid={`faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ borderBottom: `1px solid ${G.border}44`, padding: "20px 0", cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                <span style={{ fontSize: 18, color: "#fff" }}>{f.q}</span>
                <ChevronDown size={18} color={G.gold} style={{ transform: openFaq === i ? "rotate(180deg)" : "none", transition: "transform .2s", flexShrink: 0 }} />
              </div>
              {openFaq === i && <div style={{ color: G.muted, fontSize: 15, marginTop: 12, lineHeight: 1.7, fontStyle: "italic" }}>{f.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section style={{ padding: "70px 5vw", borderTop: `1px solid ${G.border}33`, maxWidth: 680, margin: "0 auto", textAlign: "center" }}>
        <Mail size={24} color={G.gold} style={{ margin: "0 auto 12px" }} />
        {invitedRef && <div data-testid="invited-note" className="mono" style={{ fontSize: 10, letterSpacing: ".18em", color: G.gold, marginBottom: 10 }}>✦ A FRIEND INVITED YOU ✦</div>}
        <h2 style={{ ...heading, margin: "0 auto", fontSize: "clamp(24px,3.5vw,36px)" }}>Get new genres & drops first.</h2>
        {subDone ? (
          <div data-testid="sub-done" className="mono" style={{ fontSize: 12, letterSpacing: ".12em", color: G.gold, marginTop: 20 }}>✦ YOU'RE SUBSCRIBED ✦</div>
        ) : (
          <div style={{ display: "flex", gap: 8, maxWidth: 440, margin: "22px auto 0" }}>
            <input data-testid="sub-email" value={subEmail} onChange={(e) => setSubEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && subscribe()}
              placeholder="you@email.com" style={{ flex: 1, padding: "12px 16px", background: G.dark, border: `1px solid ${G.border}`, color: G.text, fontSize: 14 }} />
            <button data-testid="sub-btn" onClick={subscribe} disabled={!subEmail} className="btn-gold" style={{ padding: "0 22px", fontSize: 10 }}>Subscribe</button>
          </div>
        )}
      </section>

      {/* SUPPORT BAND */}
      <section style={{ position: "relative", overflow: "hidden", padding: "90px 5vw", borderTop: `1px solid ${G.border}33`, maxWidth: 820, margin: "0 auto", textAlign: "center" }}>
        {/* Ghosted glass watermark */}
        <div aria-hidden="true" style={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
          fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontWeight: 700,
          fontSize: "clamp(64px,15vw,200px)", lineHeight: 1, whiteSpace: "nowrap",
          color: "rgba(201,168,76,0.08)", WebkitTextStroke: "1px rgba(201,168,76,0.14)",
          letterSpacing: "0.02em", userSelect: "none", pointerEvents: "none", zIndex: 0,
        }}>
          Prompt Savant
        </div>
        <div style={{ position: "relative", zIndex: 1 }}>
          <Heart size={26} color={G.gold} style={{ margin: "0 auto 14px" }} />
          <h2 style={{ ...heading, margin: "0 auto" }}>Free forever. Tips keep it that way.</h2>
          <p style={{ color: G.muted, fontStyle: "italic", fontSize: 16, marginTop: 16, maxWidth: 560, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 }}>
            No account or upgrade required to use the free prompts — copy them into your notes and generate. If they helped you make something great, a small tip funds new genres and prompts.
          </p>
          <button data-testid="support-cta" onClick={() => setDonate(true)} className="btn-gold" style={{ padding: "14px 32px", fontSize: 11, marginTop: 26, display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Heart size={14} /> Leave a Tip
          </button>
        </div>
      </section>

      <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 300, display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
        <button data-testid="open-donate" onClick={() => setDonate(true)} className="btn-outline" style={{ padding: "11px 16px", fontSize: 10, display: "flex", alignItems: "center", gap: 8, background: G.black }}>
          <Heart size={13} /> Support
        </button>
        <button data-testid="open-feedback" onClick={() => setFb(true)} className="btn-gold" style={{ padding: "12px 18px", fontSize: 10, display: "flex", alignItems: "center", gap: 8 }}>
          <MessageSquarePlus size={14} /> Suggest
        </button>
      </div>
      <FeedbackModal open={fb} onClose={() => setFb(false)} />
      <DonationModal open={donate} onClose={() => setDonate(false)} />
      <Footer />
    </div>
  );
}

const SectionLabel = ({ children }) => (
  <div className="mono" style={{ fontSize: 9, letterSpacing: "0.5em", color: G.gold, marginBottom: 16 }}>{children}</div>
);
const heading = { fontSize: "clamp(28px,4vw,44px)", fontWeight: 300, fontStyle: "italic", color: "#fff", lineHeight: 1.15, maxWidth: 760 };
